import {
  AppColors,
  DataTable,
  DateDisplay,
  EnumValueDisplay,
  IDataTableColumn,
  ShowMoreButton,
} from '@lib/common';
import { Box, Tooltip, Typography } from '@material-ui/core';
import { InvoiceRun, InvoiceRunStatus, invoiceStatusService } from 'app/state';
import React from 'react';
import { useHistory } from 'react-router-dom';

interface Props {
  runs: Array<InvoiceRun>;
  loading: boolean;
  isDone: boolean;
  loadMore: () => void;
}

function renderCell(value?: string | null | number) {
  return <Typography>{value}</Typography>;
}

function renderStatusCell(run: InvoiceRun) {
  const getColor = () => {
    switch (run.runStatus) {
      case InvoiceRunStatus.Complete:
        return AppColors.Green;
      case InvoiceRunStatus.Running:
        return AppColors.Blue;
      default:
        return AppColors.Gray;
    }
  };

  return (
    <Tooltip title={run.runStatus ?? ''} arrow>
      <Box
        style={{ borderRadius: '50%', backgroundColor: getColor(), width: '1em', height: '1em' }}
      />
    </Tooltip>
  );
}

const columns: Array<IDataTableColumn<InvoiceRun>> = [
  {
    title: '',
    valueFactory: run => renderStatusCell(run),
    colSpan: 0.25,
  },
  {
    title: 'Start Date',
    valueFactory: run => (
      <DateDisplay date={new Date(run.invoiceStartDate ?? '')} dateFormat="M/d/yy" />
    ),
  },
  {
    title: 'End Date',
    valueFactory: run => (
      <DateDisplay date={new Date(run.invoiceEndDate ?? '')} dateFormat="M/d/yy" />
    ),
  },
  {
    title: 'Generated Date',
    valueFactory: run => (
      <DateDisplay date={new Date(run.createdDate ?? '')} dateFormat="M/d/yy h:mm a" />
    ),
  },
  {
    title: 'Invoice Type',
    valueFactory: run => <EnumValueDisplay value={run.invoiceType} />,
  },
  {
    title: 'Billing Frequency',
    valueFactory: run => <EnumValueDisplay value={run.billingFrequencyType} />,
  },
  {
    title: 'Invoices Created',
    valueFactory: run => renderCell(run.invoicesCreated),
  },
  {
    title: 'Invoices Requested',
    valueFactory: run => renderCell(run.invoicesRequested),
  },
  {
    title: 'Version',
    valueFactory: run => renderCell(run.isDraft ? 'Draft' : 'Final'),
  },
];

export function InvoiceRunList({ runs, loading, isDone, loadMore }: Props) {
  const history = useHistory();
  const onRowClick = (r: InvoiceRun) => {
    invoiceStatusService.reset();
    const path = `corporate-accounts/run/${r.id}`;
    history.push(path);
  };

  return (
    <Box mt={2}>
      <DataTable
        items={runs}
        columns={columns}
        getItemKey={r => r.id ?? ''}
        loading={loading}
        onRowClick={onRowClick}
      >
        <ShowMoreButton isDone={isDone} loadMore={loadMore} />
      </DataTable>
    </Box>
  );
}
